import { useEffect, useState } from "react";
import { ArrowUpRight, Heart } from "lucide-react";
import { DEFAULT_SUPPORT_BLURB, getSupportConfig, openSupportPage, type SupportableAthlete } from "@/lib/support";
import { trackEvent } from "@/lib/analytics";
import { sendEvent } from "@/lib/firstPartyAnalytics";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Opens the existing confirmation step (used by the Back Her Game chooser). */
export const OPEN_SUPPORT_CONFIRM_EVENT = "siis:open-support-confirm";

export type PublicSupporter = {
  id: string;
  supporter_name: string | null;
  message: string | null;
  transaction_timestamp: string;
};

type Props = {
  athlete: SupportableAthlete & { id: string; name: string; first_name?: string | null };
  /** Consented supporters the viewer is allowed to read. Supplied by the page. */
  supporters?: PublicSupporter[];
  /** Public Supporter Wall readiness, decided centrally in profileModules. */
  showSupporterWall?: boolean;
};

const SupportSection = ({ athlete, supporters = [], showSupporterWall = false }: Props) => {
  const config = getSupportConfig(athlete);
  const firstName = athlete.first_name?.trim() || athlete.name.split(" ")[0];
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [handoffComplete, setHandoffComplete] = useState(false);

  useEffect(() => {
    const open = () => {
      trackEvent("support_intent_click", { provider: config.provider });
      setConfirmOpen(true);
    };
    window.addEventListener(OPEN_SUPPORT_CONFIRM_EVENT, open);
    return () => window.removeEventListener(OPEN_SUPPORT_CONFIRM_EVENT, open);
  }, [config.provider]);

  // Readiness is decided by the caller; this guard stays as a second line of defence.
  if (!config.enabled) return null;

  return (
    <section id="support" className="relative overflow-hidden rounded-[2rem] bg-secondary text-secondary-foreground shadow-bold">
      <div className="p-6 md:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-on-navy">Back Her Journey</p>
        <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mt-2 text-balance break-words">
          Back {athlete.name}
        </h2>
        <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed opacity-90">
          {athlete.support_blurb?.trim() || DEFAULT_SUPPORT_BLURB}
        </p>

        <div className="mt-7">
          <button
            type="button"
            onClick={() => {
              trackEvent("support_intent_click", { provider: config.provider });
              setConfirmOpen(true);
            }}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full bg-primary text-primary-foreground font-display text-lg uppercase tracking-wide hover:bg-background hover:text-foreground transition-colors shadow-bold"
          >
            <Heart className="w-5 h-5" aria-hidden />
            Back {firstName}
            <ArrowUpRight className="w-5 h-5" aria-hidden />
          </button>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Here's what happens next</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-left">
                    <p>
                      We'll open {firstName}'s secure payment page in a new tab, where you choose the
                      amount and whether it's one-time or monthly.
                    </p>
                    <p>
                      Your support goes directly to her — Say It In Sports never handles the payment or
                      sees your card details.
                    </p>
                    <p>
                      Your name and message only appear on her profile if you choose to be shown
                      publicly.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Not now</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    trackEvent("support_handoff", { provider: config.provider });
                    sendEvent("direct_support_click", { athleteId: athlete.id, metadata: { provider: config.provider } });
                    openSupportPage(config);
                    setHandoffComplete(true);
                  }}
                >
                  Continue to payment
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <p className="mt-3 text-sm opacity-80 max-w-md leading-relaxed">
            Support is paid directly to the athlete and is not a charitable donation.
          </p>
          <p className="mt-2 text-xs opacity-70 max-w-md leading-relaxed">
            Secure payment processing is provided by a third-party payment provider. Support is paid directly to the athlete.
          </p>
          {handoffComplete && (
            <div className="mt-4 max-w-xl rounded-lg border border-secondary-foreground/20 bg-secondary-foreground/10 p-4" role="status">
              <p className="font-semibold">Payment page opened</p>
              <p className="mt-1 text-sm opacity-80">If you completed payment, thank you. Confirmation is handled securely by the payment provider and may take a few minutes to appear; this page cannot confirm completion.</p>
            </div>
          )}
        </div>

        {showSupporterWall && supporters.length > 0 && (
          <div className="mt-8 border-t border-secondary-foreground/15 pt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">In her corner</p>
            <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {supporters.map((s) => (
                <li key={s.id} className="rounded-2xl bg-secondary-foreground/10 px-4 py-3 min-w-0">
                  <p className="text-sm font-semibold break-words">{s.supporter_name || "A supporter"}</p>
                  {s.message && <p className="text-xs opacity-80 mt-1 line-clamp-3 break-words">{s.message}</p>}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] opacity-60">
              Supporters appear here only when they choose to be shown publicly.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default SupportSection;
